import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import { ThemeProvider } from './context/ThemeContext';
import { UserProvider } from './context/UserContext';
import { ProtectedRoute } from './components/Auth';
import SignupAttribution from './components/Auth/SignupAttribution';
import BetaTipsCard from './components/Auth/BetaTipsCard';
import AuthenticatedApiProvider from './components/Auth/AuthenticatedApiProvider';
import BrainstormyLayout from './components/Layout/BrainstormyLayout';
import { ClaimBookMemory, RememberBookPage, ResumeOrProjects, UnknownPathRedirect } from './components/Layout/ResumeBookPage';
import ChatPage from './pages/ChatPage';
import ChatSessionsPage from './pages/ChatSessionsPage';
import ProjectsPage from './pages/ProjectsPage';
import BookmarksPage from './pages/BookmarksPage';
import BiblesPage from './pages/BiblesPage';
import SearchPage from './pages/SearchPage';
import PlotTrackPage from './pages/PlotTrackPage';
import PathPage from './pages/PathPage';
import BringIntoBookPage from './pages/BringIntoBookPage';
import StyleCardsPage from './pages/StyleCardsPage';
import StoryRulesPage from './pages/StoryRulesPage';
import SceneWritingPage from './pages/SceneWritingPage';
import DossiersPage from './pages/DossiersPage';
import FrameworkBuilderPage from './pages/FrameworkBuilderPage';
import SeriesOutlinePage from './pages/SeriesOutlinePage';
// Project Sessions: Import project-level pages
import ProjectBookmarksPage from './pages/ProjectBookmarksPage';
// Onboarding
import OnboardingPage from './pages/OnboardingPage';
// Auth pages
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
// Admin pages
import DiscoveryPage from './pages/DiscoveryPage';
import SeriesBiblePage from './pages/SeriesBiblePage';
import SeriesLandingRedirect from './pages/SeriesLandingRedirect';
import SeriesBookBoardPage from './pages/SeriesBookBoardPage';
import ProjectNotesPage from './pages/ProjectNotesPage';
import BookNotesPage from './pages/BookNotesPage';
import SeriesCardsPage from './pages/SeriesCardsPage';
import SeriesDossiersPage from './pages/SeriesDossiersPage';
import SeriesOffersPage from './pages/SeriesOffersPage';
import DocsCharacterOffersPage from './pages/DocsCharacterOffersPage';
import RequireSeriesProject from './components/Series/RequireSeriesProject';
import AdminPage from './pages/AdminPage';
import AdminRoute from './components/Admin/AdminRoute';
// The Editing Room — a separate room, not a Story Board panel: no
// persistent outline sidebar, no BrainstormyLayout chrome. Sibling to
// /admin below, not nested in ProtectedLayout.
import EditingRoomPage from './pages/EditingRoomPage';
// Orientation Dashboard — replaces the StoryGraph panel at /graph (swap, not add).
import OrientationDashboard from './components/Orientation/OrientationDashboard';
import GraphHistoryStub from './components/RelationshipGraph/GraphHistoryStub';
// Engagement Messaging
import { EngagementMessageProvider } from './context/EngagementMessageContext';
import { HintsProvider } from './context/HintsContext';
import { EngagementMessageModal } from './components/EngagementMessage';
import { SourceViewerProvider } from './components/DocumentImport/SourceViewerContext';
import { plottrackPath } from './utils/plottrackPath';

/**
 * Route guard that redirects to onboarding if not completed.
 */
function RequireOnboarding({ children }) {
  const { onboardingComplete } = useApp();
  const location = useLocation();

  if (!onboardingComplete) {
    // Save intended destination for potential redirect after onboarding
    return <Navigate to="/onboarding" state={{ from: location }} replace />;
  }

  return children;
}

/**
 * Old Series Notes /dashboard is not a working front door (#670 / #682).
 * Apply the first-open lock: empty → Series Discovery, pins → Timeline.
 * The sheet and its data stay unused.
 */
function LegacySeriesDashboardRedirect() {
  const { projectId } = useParams();
  return <SeriesLandingRedirect projectId={projectId} />;
}

function BoardToPlotTrackRedirect() {
  const { storyId } = useParams();
  const { currentStory } = useApp();
  return <Navigate to={plottrackPath(storyId || currentStory?.id)} replace />;
}

function OutlineToPlotTrackRedirect() {
  const { currentStory } = useApp();
  return <Navigate to={plottrackPath(currentStory?.id)} replace />;
}

function PathToTasksRedirect() {
  const { storyId } = useParams();
  return <Navigate to={storyId ? `/tasks/${storyId}` : '/tasks'} replace />;
}

/**
 * Layout wrapper for protected routes.
 * Requires authentication first, then onboarding completion.
 */
function ProtectedLayout() {
  return (
    <ProtectedRoute>
      <RequireOnboarding>
        <SourceViewerProvider>
          <BrainstormyLayout />
        </SourceViewerProvider>
      </RequireOnboarding>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthenticatedApiProvider>
        <UserProvider>
          <AppProvider>
            <BrowserRouter>
              <SignupAttribution />
              <BetaTipsCard />
              <EngagementMessageProvider>
              <HintsProvider>
              <ClaimBookMemory />
              <RememberBookPage />
              <Routes>
                {/* Auth routes (no auth required) */}
                <Route path="/sign-in/*" element={<SignInPage />} />
                <Route path="/sign-up/*" element={<SignUpPage />} />

                {/* Onboarding route (requires auth but no onboarding check) */}
                <Route path="/onboarding" element={
                  <ProtectedRoute>
                    <OnboardingPage />
                  </ProtectedRoute>
                } />

                {/* All other routes require auth and onboarding completion */}
                <Route element={<ProtectedLayout />}>
                  {/* Entry point: back to the page left in the open book (run 43),
                      else the all-projects landing page (sign-in lands here). */}
                  <Route path="/" element={<ResumeOrProjects />} />
                  <Route path="/projects" element={<ProjectsPage />} />

                  {/* Chat routes (work for both story and project sessions) */}
                  <Route path="/chat" element={<ChatPage />} />
                  <Route path="/chat/:sessionId" element={<ChatPage />} />
                  <Route path="/sessions" element={<ChatSessionsPage />} />

                  {/* Story-level routes */}
                  <Route path="/bookmarks" element={<BookmarksPage />} />
                  <Route path="/bibles" element={<BiblesPage />} />
                  <Route path="/bibles/:templateKey" element={<BiblesPage />} />
                  {/* Retired — Reports is replaced by Book Notes.
                      Silent redirect so old /reports bookmarks are not a dead
                      page and do not keep a Reports surface. */}
                  <Route path="/reports" element={<Navigate to="/bibles" replace />} />
                  <Route path="/reports/:reportId" element={<Navigate to="/bibles" replace />} />
                  <Route path="/search" element={<SearchPage />} />
                  {/* Retired standalone Outline page. PlotTrack is the outline door. */}
                  <Route path="/outline" element={<OutlineToPlotTrackRedirect />} />

                  {/* Plot notebook room (#917). Character leftover URLs still land on Book Notes. */}
                  <Route path="/dossiers" element={<DossiersPage />} />
                  <Route path="/frameworks" element={<FrameworkBuilderPage />} />

                  {/* PlotTrack is the writer-facing outline door (#817).
                      /board is not a leftover Story Board fallback. */}
                  <Route path="/plottrack" element={<PlotTrackPage />} />
                  <Route path="/plottrack/:storyId" element={<PlotTrackPage />} />
                  <Route path="/tasks" element={<PathPage />} />
                  <Route path="/tasks/:storyId" element={<PathPage />} />
                  <Route path="/path" element={<PathToTasksRedirect />} />
                  <Route path="/path/:storyId" element={<PathToTasksRedirect />} />
                  <Route path="/bring-into-book" element={<BringIntoBookPage />} />
                  <Route path="/bring-into-book/:storyId" element={<BringIntoBookPage />} />
                  <Route path="/style-cards" element={<StyleCardsPage />} />
                  <Route path="/style-cards/:storyId" element={<StyleCardsPage />} />
                  <Route path="/story-rules" element={<StoryRulesPage />} />
                  <Route path="/story-rules/:storyId" element={<StoryRulesPage />} />
                  <Route path="/scene-writing" element={<SceneWritingPage />} />
                  <Route path="/scene-writing/:storyId" element={<SceneWritingPage />} />
                  <Route path="/board" element={<OutlineToPlotTrackRedirect />} />
                  <Route path="/board/:storyId" element={<BoardToPlotTrackRedirect />} />

                  {/* Orientation Dashboard (replaces StoryGraph at the same route) */}
                  <Route path="/graph" element={<OrientationDashboard />} />
                  <Route path="/graph/history" element={<GraphHistoryStub />} />

                  {/* Discovery route */}
                  <Route path="/projects/:projectId/discovery" element={<DiscoveryPage />} />
                  {/* Old Series Notes sheet is not a working front door (#670).
                      Historical data stays. Redirect to Series Discovery. */}
                  <Route
                    path="/projects/:projectId/dashboard"
                    element={<LegacySeriesDashboardRedirect />}
                  />
                  <Route
                    path="/projects/:projectId/series"
                    element={
                      <RequireSeriesProject>
                        <SeriesBookBoardPage />
                      </RequireSeriesProject>
                    }
                  />
                  {/* Series Docs door (#672). Timeline/Docs toggle.
                      Same import pile as Book Planning Docs (#823). */}
                  <Route
                    path="/projects/:projectId/notes"
                    element={<ProjectNotesPage />}
                  />
                  {/* Book Planning Docs (#823). Same pile. Stays on
                      the book map. Not a series mode-switch. */}
                  <Route
                    path="/projects/:projectId/stories/:storyId/notes"
                    element={<BookNotesPage />}
                  />
                  <Route
                    path="/projects/:projectId/stories/:storyId/docs-offers"
                    element={<DocsCharacterOffersPage />}
                  />
                  <Route
                    path="/projects/:projectId/docs-offers"
                    element={<DocsCharacterOffersPage />}
                  />
                  {/* Series Notes (#676 / #696). One Snapshots room.
                      Not the leftover /dashboard sheet. Leftover
                      /series-dossiers redirects into this pile. */}
                  <Route
                    path="/projects/:projectId/series-cards"
                    element={
                      <RequireSeriesProject>
                        <SeriesCardsPage />
                      </RequireSeriesProject>
                    }
                  />
                  <Route
                    path="/projects/:projectId/series-dossiers"
                    element={
                      <RequireSeriesProject>
                        <SeriesDossiersPage />
                      </RequireSeriesProject>
                    }
                  />
                  <Route
                    path="/projects/:projectId/series-offers"
                    element={
                      <RequireSeriesProject>
                        <SeriesOffersPage />
                      </RequireSeriesProject>
                    }
                  />

                  {/* Project Sessions: Project-level routes */}
                  <Route path="/project/bookmarks" element={<ProjectBookmarksPage />} />
                  <Route path="/series-outline" element={<SeriesOutlinePage />} />
                  {/* Retired standalone route — redirects to Series
                      Discovery. Series Bible is not a working door (#670).
                      No RequireSeriesProject wrapper needed: SeriesBiblePage
                      only redirects. */}
                  <Route path="/series-bible" element={<SeriesBiblePage />} />

                  {/* Unknown paths (/novels, /home) are not a blank screen. */}
                  <Route path="*" element={<UnknownPathRedirect />} />

                </Route>

                {/* Admin route - requires admin role */}
                <Route path="/admin" element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <AdminPage />
                    </AdminRoute>
                  </ProtectedRoute>
                } />

                {/* The Editing Room — entered only via the Story Board's
                    "Draft →" door. No BrainstormyLayout (its own slim top
                    bar), same auth requirement as everything else. */}
                <Route path="/editing-room/:sceneId" element={
                  <ProtectedRoute>
                    <EditingRoomPage />
                  </ProtectedRoute>
                } />
              </Routes>
              <EngagementMessageModal />
              </HintsProvider>
              </EngagementMessageProvider>
            </BrowserRouter>
          </AppProvider>
        </UserProvider>
      </AuthenticatedApiProvider>
    </ThemeProvider>
  );
}

export default App;
